import { ThemeProvider } from "@/providers/ThemeProvider";
import { QueryProvider } from "@/providers/QueryProvider";
import { CookieConsentProvider } from "@/providers/CookieConsentProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/providers/AuthProvider";
import { configureAuth } from "@/lib/auth-fetch";
import { setApiUrl } from "@/lib/api-config";
import { getStoredCompany } from "@/features/multi-company/utils/company-storage";
import { TourProvider } from "@/contexts/TourContext";
import { ProductTour } from "@/components/ProductTour";
import { WootricProvider } from "@/providers/WootricProvider";
import { IntercomProvider } from "@/providers/IntercomProvider";
import { useUserProfile } from "@/hooks/useUserProfile";
import App from "@/App";
import { useEffect } from "react";
import type { IntercomUser } from "@/types/intercom";
interface AppContainerProps {
  settings?: {
    apiUrl?: string;
    clientId?: string;
    defaultUrl?: string;
    identityUrl?: string;
    redirectUri?: string;
    scope?: string;
    workspaceUrl?: string;
  };
}

function useIntercomUser(): { intercomUser: IntercomUser | null } {
  const { instanceId } = useAuth();
  const { user: userProfile } = useUserProfile();

  if (!userProfile?.email || !instanceId) {
    return { intercomUser: null };
  }

  return {
    intercomUser: {
      email: userProfile.email,
      instanceId,
      name: userProfile.organisationName,
    },
  };
}

export default function AppContainer({ settings }: AppContainerProps = {}): React.ReactElement {
  const { getAccessToken, instanceId } = useAuth();
  const { user: wootricUser } = useUserProfile();
  const { intercomUser } = useIntercomUser();

  // Set apiUrl synchronously before render to ensure it's available for API calls
  if (settings?.apiUrl) {
    setApiUrl(settings.apiUrl);
  }

  useEffect(() => {
    // Read evoOrganisationId from sessionStorage (set by useAuthenticatedEmployee hook)
    const evoOrganisationId = sessionStorage.getItem('evo_organisation_id') || undefined;
    // Read stored company ID from localStorage for active company selection
    const companyId = getStoredCompany() || undefined;
    configureAuth({ getAccessToken, instanceId, evoOrganisationId, companyId });
  }, [getAccessToken, instanceId]);

  return (
    <QueryProvider>
      <CookieConsentProvider>
        <TooltipProvider>
          <ThemeProvider attribute="class" defaultTheme="light" disableTransitionOnChange>
            <TourProvider>
              <WootricProvider user={wootricUser}>
                <IntercomProvider user={intercomUser}>
                  <Toaster duration={5000} closeButton />
                  <ProductTour />
                  <App />
                </IntercomProvider>
              </WootricProvider>
            </TourProvider>
          </ThemeProvider>
        </TooltipProvider>
      </CookieConsentProvider>
    </QueryProvider>
  );
}
